// Hover sounds share the same peak volume, including dynamically loaded cards.
function normalizeSound(buffer) {
    if (!buffer) return buffer;
    let peak = 0;
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const samples = buffer.getChannelData(channel);
        for (const sample of samples) peak = Math.max(peak, Math.abs(sample));
    }
    if (!peak) return buffer;
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const samples = buffer.getChannelData(channel);
        for (let index = 0; index < samples.length; index++) samples[index] /= peak;
    }
    return buffer;
}

export function hoverSound() {
    const desktop = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
    if (!desktop.matches) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const soundUrl = window.hokraData?.hoverSoundUrl;
    if (!AudioContext || !soundUrl) return;

    // Load each sound once; decode it after a user gesture unlocks audio.
    const loadSound = (url) => fetch(url)
        .then((response) => {
            if (!response.ok) throw new Error('Hover sound unavailable');
            return response.arrayBuffer();
        })
        .catch(() => null);
    const audioData = loadSound(soundUrl);
    const ctaAudioData = window.hokraData?.ctaSoundUrl
        ? loadSound(window.hokraData.ctaSoundUrl) : Promise.resolve(null);
    const marqueeAudioData = window.hokraData?.marqueeSoundUrl
        ? loadSound(window.hokraData.marqueeSoundUrl) : Promise.resolve(null);

    const selector = [
        'a[href]',
        '[data-btn]', '[data-marquee-button]', '.marquee-button',
        'button', '.btn', '.header__logo', '.nav-item', '.menu-item a',
        '.side-menu a', '.card', '.project-card', '.team-card',
        '.service-card', '.product-card', '.blog-card', '.job-card',
        '.challenge-card', '[data-hover-sound]',
    ].join(', ');
    let context;
    let buffer;
    let ctaBuffer;
    let marqueeBuffer;
    let clickReady;
    let soundsReady;
    let toggleVersion = 0;
    let enabled = false;
    const storageKey = 'hokra-hover-sound-enabled';
    try {
        enabled = window.sessionStorage.getItem(storageKey) === 'true';
    } catch {
        // Keep the switch available when browser storage is unavailable.
    }
    const activeSources = new Set();
    let lastPlayed = -Infinity;

    // Unlock audio during a browser-approved user gesture.
    function unlock(event) {
        if (!enabled || !desktop.matches || (event && !event.isTrusted)) return;
        try {
            if (!context) {
                context = new AudioContext();
                clickReady = audioData.then((data) => data ? context.decodeAudioData(data) : null)
                    .then((decoded) => { buffer = normalizeSound(decoded); })
                    .catch(() => {});
                const ctaReady = ctaAudioData.then((data) => data ? context.decodeAudioData(data) : null)
                    .then((decoded) => { ctaBuffer = normalizeSound(decoded); })
                    .catch(() => {});
                const marqueeReady = marqueeAudioData.then((data) => data ? context.decodeAudioData(data) : null)
                    .then((decoded) => { marqueeBuffer = normalizeSound(decoded); })
                    .catch(() => {});
                soundsReady = Promise.all([clickReady, ctaReady, marqueeReady]);
            }
            const resumed = context.state !== 'running' && context.state !== 'closed'
                ? context.resume().catch(() => {}) : Promise.resolve();
            return Promise.all([soundsReady, resumed]);
        } catch {
            // Audio is optional; interaction remains available if it is blocked.
        }
    }

    function onHover(event) {
        if (!enabled) return;
        if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
        const target = event.target.closest(selector);
        if (!target || target.matches(':disabled, [aria-disabled="true"]') ||
            target.closest('[data-hover-sound="off"]')) return;
        // Moving between children of the same target should not repeat the click.
        if (event.relatedTarget instanceof Node && target.contains(event.relatedTarget)) return;
        if (!context || context.currentTime - lastPlayed < 0.075) return;
        playSound(soundFor(target));
    }

    function soundFor(target) {
        return target.matches('[data-marquee-button], .marquee-button')
            ? marqueeBuffer
            : target.matches('.cta__button, .cta .btn, .cta button') ? ctaBuffer : buffer;
    }

    function playSound(soundBuffer) {
        if (!enabled || !desktop.matches || !context || !soundBuffer || context.state !== 'running' || document.hidden) return;
        const now = context.currentTime;
        lastPlayed = now;

        const source = context.createBufferSource();
        const gain = context.createGain();
        source.buffer = soundBuffer;
        gain.gain.value = 0.2;
        source.connect(gain);
        gain.connect(context.destination);
        source.onended = () => {
            activeSources.delete(source);
            source.disconnect();
            gain.disconnect();
        };
        activeSources.add(source);
        source.start();
    }

    function updateToggles() {
        document.querySelectorAll('[data-sound-toggle]').forEach((button) => {
            button.setAttribute('aria-pressed', String(enabled));
            button.title = enabled ? 'Geluid uitschakelen' : 'Geluid inschakelen';
        });
    }

    updateToggles();
    if (enabled) unlock();

    document.querySelectorAll('[data-sound-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', (event) => {
            enabled = !enabled;
            const version = ++toggleVersion;
            try {
                window.sessionStorage.setItem(storageKey, String(enabled));
            } catch {
                // Audio still works for the current page without storage.
            }
            updateToggles();
            if (enabled) {
                const ready = unlock(event);
                if (ready) ready.then(() => {
                    if (enabled && version === toggleVersion) playSound(buffer);
                });
            } else {
                activeSources.forEach((source) => source.stop());
            }
        });
    });
    // Resume a restored preference if the browser requires a fresh user gesture.
    document.addEventListener('pointerdown', unlock, { passive: true });
    document.addEventListener('keydown', unlock);
    document.addEventListener('pointerover', onHover, { passive: true });
}
